import { Link } from "@inertiajs/react"
import { ArrowRight, Coins } from "lucide-react"
import { useTranslation } from "react-i18next"

import { RankPatch } from "@/components/rank/rank-patch"
import { cn } from "@/lib/utils"
import { creatorMissionsPath } from "@/routes"

/**
 * OKS-556 — §19 WalletHero (UX Spec §2.1). The gold-gradient identity + coin
 * balance block at the top of `/rewards`.
 *
 * Blends the two economies (PRD §3): the large number is the creator's
 * SPENDABLE coin balance (coin ledger), while the RankPatch + named-rank chip
 * are IDENTITY from the XP economy — earned, never deducted on spend. Below
 * sits a whole-row "earn → missions" hint (js-routes, ≥44px touch target).
 *
 * `rank` is nil only for a mis-seeded brand (no ladder) — the patch + chip are
 * then omitted; the balance still renders (Empty-state contract, UX §3).
 */

export interface WalletRank {
  name: string
  tier: number
  tier_count: number
}

interface WalletHeroProps {
  balance: number
  rank: WalletRank | null
  className?: string
}

export function WalletHero({ balance, rank, className }: WalletHeroProps) {
  const { t } = useTranslation()

  return (
    <section
      data-testid="wallet-hero"
      className={cn(
        "overflow-hidden rounded-2xl bg-gradient-to-b from-amber-800 to-orange-900 text-center",
        className,
      )}
    >
      <div className="px-6 py-7">
        <p className="text-xs font-semibold tracking-widest text-amber-200 uppercase">
          {t("rewards.creator.wallet.balance_label")}
        </p>

        <div className="mt-1 flex items-end justify-center gap-2">
          <Coins aria-hidden="true" className="mb-1.5 size-7 text-amber-300" />
          <span
            className="text-5xl font-bold text-white tabular-nums"
            data-testid="wallet-hero-balance"
          >
            {balance.toLocaleString()}
          </span>
          <span className="mb-1.5 text-lg font-medium text-amber-200">
            {t("rewards.creator.wallet.unit")}
          </span>
        </div>

        <p className="mt-1 text-sm text-amber-100/80">
          {t("rewards.creator.wallet.spendable")}
        </p>

        {rank && (
          <div
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-black/20 py-1 pr-3 pl-1"
            data-testid="wallet-hero-rank"
          >
            <RankPatch
              tier={rank.tier}
              tierCount={rank.tier_count}
              name={rank.name}
              size="md"
            />
            <span className="text-sm font-semibold text-amber-50">
              {rank.name}
            </span>
          </div>
        )}
      </div>

      {/* Earn → missions hint: the whole row is the tap target (≥44px). */}
      <Link
        href={creatorMissionsPath()}
        data-testid="wallet-hero-earn-hint"
        className="flex items-center justify-center gap-1.5 border-t border-amber-100/15 bg-black/10 px-6 py-3 text-sm font-medium text-amber-100 transition-colors hover:bg-black/20 hover:text-white"
      >
        {t("rewards.creator.wallet.earn_hint")}
        <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </section>
  )
}
